import { fetchWithAuth } from '../lib/fetchWithAuth';
import { isEmployerVerified } from '../lib/jobVerification';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

export type SavedJob = {
  id: string | number;
  title: string;
  company: string;
  location: string;
  salary?: string;
  type?: string;
  posted?: string;
  matchScore?: number;
  description?: string;
  requirements?: string[];
  benefits?: string[];
  commute?: string;
  verified?: boolean;
  urgent?: boolean;
  savedDate?: string;
};

const applyVerification = (job: SavedJob | (SavedJob & Record<string, unknown>)): SavedJob => ({
  ...job,
  verified: isEmployerVerified(job),
});

export async function fetchSavedJobs(): Promise<SavedJob[]> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to fetch saved jobs.');
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/saved-jobs`);
  if (!response.ok) {
    throw new Error(`Saved jobs fetch failed (${response.status})`);
  }
  const payload = await response.json();
  if (Array.isArray(payload?.data)) {
    return (payload.data as SavedJob[]).map(applyVerification);
  }
  if (Array.isArray(payload)) {
    return (payload as SavedJob[]).map(applyVerification);
  }
  throw new Error('Saved jobs response malformed.');
}

export async function removeSavedJob(jobId: string | number): Promise<{ success: boolean }> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to remove a saved job.');
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/saved-jobs/${jobId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Remove saved job failed (${response.status}): ${errorText}`);
  }

  return { success: true };
}

export async function saveJob(jobId: string | number): Promise<SavedJob> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to save a job.');
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/saved-jobs`, {
    method: 'POST',
    body: JSON.stringify({ jobId }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Save job failed (${response.status}): ${errorText}`);
  }

  const payload = await response.json();
  if (payload && typeof payload === 'object') {
    return applyVerification({
      id: payload.id ?? jobId,
      title: payload.title ?? payload.jobTitle ?? 'Saved job',
      company: payload.company ?? payload.companyName ?? 'Unknown Company',
      location: payload.location ?? '',
      salary: payload.salary,
      type: payload.type,
      posted: payload.posted,
      matchScore: payload.matchScore,
      description: payload.description,
      requirements: payload.requirements ?? [],
      benefits: payload.benefits ?? [],
      commute: payload.commute,
      urgent: payload.urgent,
      savedDate: payload.savedDate ?? new Date().toISOString().split('T')[0],
    });
  }

  throw new Error('Save job response malformed');
}
